import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnalysisSectionHeader } from '../../AnalysisSectionHeader'
import {
  RentalMarketProvider,
  useRentalMarket,
  type RentalMarketService,
} from './RentalMarketContext'
import { StatsSection } from '../_shared/stubs'
import { RentalPriceEvolution } from './components/sections/RentalPriceEvolution'
import { RentalPriceM2Evolution } from './components/sections/RentalPriceM2Evolution'
import { RentalDistribution } from './components/sections/RentalDistribution'
import { RentalPricePerRoom } from './components/sections/RentalPricePerRoom'
import { RentalPriceM2PerRoom } from './components/sections/RentalPriceM2PerRoom'
import { RentalMarketSummaryMetrics } from './components/sections/RentalMarketSummaryMetrics'
import { Icon } from '../../atoms/Icon'
import { PriceModeToggle } from '../../molecules/PriceModeToggle'
import { FilterChip, FilterDisplay, type FilterItem } from '../../molecules/FilterDisplay'
import {
  StatsPageContainer,
  StatsContent,
  HeaderSection,
  SectionsContainer,
  PlaceholderChart,
  FilterSection,
  FilterBar,
  LocationFilters,
  LocationBadge,
  AdvancedFiltersToggle,
  AdvancedFiltersPanel,
  AdvancedFiltersHeader,
  AdvancedFilters,
} from './RentalMarketSection.styles'
import { AnalysisTabContentContainer } from '../../molecules/SectionAnalysisStyles'

type Period = 'all' | 'last-12-months' | 'since-2022'

interface Section {
  id: string
  titleKey: string
  descriptionKey: string
  period: Period
}

const rentalMarketSections: Section[] = [
  {
    id: 'rental-price-m2-evolution',
    titleKey: 'rental.sections.priceM2Evolution.title',
    descriptionKey: 'rental.sections.priceM2Evolution.description',
    period: 'all',
  },
  {
    id: 'rental-price-evolution',
    titleKey: 'rental.sections.priceEvolution.title',
    descriptionKey: 'rental.sections.priceEvolution.description',
    period: 'all',
  },
  {
    id: 'rental-distribution',
    titleKey: 'rental.sections.distribution.title',
    descriptionKey: 'rental.sections.distribution.description',
    period: 'last-12-months',
  },
  {
    id: 'rental-price-per-room',
    titleKey: 'rental.sections.pricePerRoom.title',
    descriptionKey: 'rental.sections.pricePerRoom.description',
    period: 'last-12-months',
  },
  {
    id: 'rental-price-m2-per-room',
    titleKey: 'rental.sections.priceM2PerRoom.title',
    descriptionKey: 'rental.sections.priceM2PerRoom.description',
    period: 'last-12-months',
  },
]

interface RentalMarketContentProps {
  entity: any
  readOnly?: boolean
}

const RentalMarketContent: React.FC<RentalMarketContentProps> = ({ entity, readOnly = false }) => {
  const { t } = useTranslation('market')
  const {
    filters,
    updateLocationType,
    updatePropertyType,
    priceMode,
    updatePriceMode,
    getLocationDisplay,
  } = useRentalMarket()
  const contentRef = useRef<HTMLDivElement>(null)
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(true)

  const getLocationTypeLabel = () => {
    switch (filters.locationType) {
      case 'postal_code':
        return t('market.common.filters.postalCode')
      case 'city':
        return t('market.common.filters.city')
      case 'district':
        return t('market.common.filters.district')
      default:
        return ''
    }
  }

  // const [sectionVisibility, setSectionVisibility] = useState<
  //   Record<string, boolean>
  // >(() => {
  //   const initialVisibility: Record<string, boolean> = {}
  //   rentalMarketSections.forEach((section) => {
  //     initialVisibility[section.id] = true
  //   })
  //   return initialVisibility
  // })

  // const toggleSectionVisibility = (sectionId: string) => {
  //   setSectionVisibility((prev) => ({
  //     ...prev,
  //     [sectionId]: !prev[sectionId],
  //   }))
  // }

  return (
    <StatsPageContainer>
      <StatsContent ref={contentRef}>
        <HeaderSection>
          <AnalysisSectionHeader
            title={t('rental.header.title')}
            subtitle={t('rental.header.subtitle')}
          />
        </HeaderSection>

        <AnalysisTabContentContainer>
          {readOnly ? (
            // Read-only filter display for public view
            <FilterDisplay
              filters={
                [
                  // Location filter - show the active location with type
                  ...(entity?.address_postal_code_name && filters.locationType === 'postal_code'
                    ? [
                        {
                          label: t('market.common.filters.postalCode'),
                          values: {
                            value: entity.address_postal_code_name,
                            icon: 'map-pin' as const,
                          },
                        },
                      ]
                    : entity?.municipality_name && filters.locationType === 'city'
                      ? [
                          {
                            label: t('market.common.filters.city'),
                            values: {
                              value: entity.municipality_name,
                              icon: 'building-2' as const,
                            },
                          },
                        ]
                      : entity?.district_name && filters.locationType === 'district'
                        ? [
                            {
                              label: t('market.common.filters.district'),
                              values: {
                                value: entity.district_name,
                                icon: 'map' as const,
                              },
                            },
                          ]
                        : entity?.address_postal_code_name
                          ? [
                              {
                                label: t('market.common.filters.postalCode'),
                                values: {
                                  value: entity.address_postal_code_name,
                                  icon: 'map-pin' as const,
                                },
                              },
                            ]
                          : []),
                  // Property type filter
                  ...(filters.propertyType
                    ? [
                        {
                          label: t('market.common.filters.propertyType'),
                          values: {
                            value: t(
                              `market.common.propertyTypes.${filters.propertyType.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())}`
                            ),
                            icon: 'home' as const,
                          },
                        },
                      ]
                    : []),
                ] as FilterItem[]
              }
            />
          ) : (
            <FilterSection>
              <FilterBar>
                <LocationFilters>
                  {/* Location Filter Chips */}
                  {entity?.address_postal_code_name && (
                    <FilterChip
                      active={filters.locationType === 'postal_code'}
                      onClick={() => updateLocationType('postal_code')}
                    >
                      <Icon name="map-pin" size="small" />
                      {entity.address_postal_code_name}
                    </FilterChip>
                  )}
                  {entity?.municipality_name && (
                    <FilterChip
                      active={filters.locationType === 'city'}
                      onClick={() => updateLocationType('city')}
                    >
                      <Icon name="building-2" size="small" />
                      {entity.municipality_name}
                    </FilterChip>
                  )}
                  {entity?.district_name && (
                    <FilterChip
                      active={filters.locationType === 'district'}
                      onClick={() => updateLocationType('district')}
                    >
                      <Icon name="map" size="small" />
                      {entity.district_name}
                    </FilterChip>
                  )}

                  {/* Selected location badge */}
                  <LocationBadge>
                    <Icon name="map-pin" size="small" />
                    {getLocationTypeLabel()}: {getLocationDisplay()}
                  </LocationBadge>

                  <PriceModeToggle mode={priceMode} onModeChange={updatePriceMode} />
                </LocationFilters>

                {/* Advanced Filters Toggle */}
                <AdvancedFiltersToggle
                  $expanded={showAdvancedFilters}
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                >
                  {showAdvancedFilters ? (
                    <>
                      <Icon name="x" size="small" />
                      {t('market.common.filters.hideFilters')}
                    </>
                  ) : (
                    <>
                      <Icon name="sliders" size="small" />
                      {t('market.common.filters.advancedFilters')}
                    </>
                  )}
                </AdvancedFiltersToggle>
              </FilterBar>

              {/* Advanced Filters Panel */}
              <AdvancedFiltersPanel $expanded={showAdvancedFilters}>
                <AdvancedFiltersHeader>
                  <span className="header-title">
                    <Icon name="home" size="small" />
                    {t('market.common.filters.propertyType')}
                  </span>
                </AdvancedFiltersHeader>
                <AdvancedFilters>
                  <FilterChip
                    active={filters.propertyType === 'house_apartment'}
                    onClick={() => updatePropertyType('house_apartment')}
                  >
                    <Icon name="home" size="small" />
                    {t('market.common.propertyTypes.houseApartment')}
                  </FilterChip>
                  <FilterChip
                    active={filters.propertyType === 'house'}
                    onClick={() => updatePropertyType('house')}
                  >
                    <Icon name="home" size="small" />
                    {t('market.common.propertyTypes.house')}
                  </FilterChip>
                  <FilterChip
                    active={filters.propertyType === 'apartment'}
                    onClick={() => updatePropertyType('apartment')}
                  >
                    <Icon name="building" size="small" />
                    {t('market.common.propertyTypes.apartment')}
                  </FilterChip>
                  <FilterChip
                    active={filters.propertyType === 'commercial'}
                    onClick={() => updatePropertyType('commercial')}
                  >
                    <Icon name="briefcase" size="small" />
                    {t('market.common.propertyTypes.commercial')}
                  </FilterChip>
                </AdvancedFilters>
              </AdvancedFiltersPanel>
            </FilterSection>
          )}
          <RentalMarketSummaryMetrics t={t} />

          <SectionsContainer>
            {rentalMarketSections.map((section) => (
              <StatsSection
                key={section.id}
                id={section.id}
                title={t(section.titleKey)}
                // description={t(section.descriptionKey)}
                // period={section.period}
                // location={getLocationLabel()}
                // propertyType={getPropertyTypeLabel()}
                // isHidden={!sectionVisibility[section.id]}
                // onToggleVisibility={() => toggleSectionVisibility(section.id)}
              >
                {section.id === 'rental-price-m2-evolution' ? (
                  <RentalPriceM2Evolution t={t} />
                ) : section.id === 'rental-price-evolution' ? (
                  <RentalPriceEvolution t={t} />
                ) : section.id === 'rental-distribution' ? (
                  <RentalDistribution t={t} />
                ) : section.id === 'rental-price-per-room' ? (
                  <RentalPricePerRoom t={t} />
                ) : section.id === 'rental-price-m2-per-room' ? (
                  <RentalPriceM2PerRoom t={t} />
                ) : (
                  <PlaceholderChart>
                    {t('market.common.placeholder.chartPlaceholder', {
                      section: t(section.titleKey),
                    })}
                  </PlaceholderChart>
                )}
              </StatsSection>
            ))}
          </SectionsContainer>
        </AnalysisTabContentContainer>
      </StatsContent>
    </StatsPageContainer>
  )
}

export interface RentalMarketSectionProps {
  entity: any
  service: RentalMarketService
  /** When true, hides filters for read-only public view */
  readOnly?: boolean
  /** Initial property type filter value (defaults to 'house_apartment') */
  initialPropertyType?: 'house' | 'apartment' | 'house_apartment'
  /** Initial location type filter value (defaults to 'postal_code') */
  initialLocationType?: 'postal_code' | 'city' | 'district'
  /** Called when user changes filters (location type or property type) */
  onFilterChange?: (filters: any) => void
}

export const RentalMarketSection: React.FC<RentalMarketSectionProps> = ({
  entity,
  service,
  readOnly = false,
  initialPropertyType,
  initialLocationType,
  onFilterChange,
}) => {
  return (
    <RentalMarketProvider
      landData={entity}
      service={service}
      initialPropertyType={initialPropertyType}
      initialLocationType={initialLocationType}
      onFilterChange={onFilterChange}
    >
      <RentalMarketContent entity={entity} readOnly={readOnly} />
    </RentalMarketProvider>
  )
}
